import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Settings, Languages, MessageSquare, Users, 
  TrendingUp, ArrowRight, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { language } = useLanguage();
  const [stats, setStats] = useState({
    total_services: 0,
    total_inquiries: 0,
    new_inquiries: 0,
    total_users: 0,
    total_translations: 0
  });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/inquiries`)
      ]);
      
      setStats(statsRes.data);
      setRecentInquiries(inquiriesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: language === 'en' ? 'Total Services' : 'Total Layanan',
      value: stats.total_services,
      icon: Settings,
      color: 'bg-emerald-500',
      link: '/admin/services'
    },
    {
      title: language === 'en' ? 'Lead Inquiries' : 'Pertanyaan Lead',
      value: stats.total_inquiries,
      icon: MessageSquare,
      color: 'bg-sky-500',
      link: '/admin/inquiries',
      badge: stats.new_inquiries > 0 ? stats.new_inquiries : null
    },
    {
      title: language === 'en' ? 'Translations' : 'Terjemahan',
      value: stats.total_translations,
      icon: Languages,
      color: 'bg-purple-500',
      link: '/admin/translations'
    },
    {
      title: language === 'en' ? 'Admin Users' : 'Pengguna Admin',
      value: stats.total_users,
      icon: Users,
      color: 'bg-orange-500',
      link: '/admin/users'
    }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'Dashboard' : 'Dashboard'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={language === 'en' ? 'Dashboard Overview' : 'Ikhtisar Dashboard'}>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 lg:p-8 text-white">
          <h2 className="font-heading font-bold text-2xl mb-2" data-testid="welcome-title">
            {language === 'en' ? 'Welcome to Admin Dashboard' : 'Selamat Datang di Dashboard Admin'}
          </h2>
          <p className="text-emerald-100">
            {language === 'en' 
              ? 'Manage your website content, services, and customer inquiries.'
              : 'Kelola konten website, layanan, dan pertanyaan pelanggan Anda.'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link key={index} to={stat.link} data-testid={`stat-card-${index}`}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer relative overflow-hidden">
                  {stat.badge && (
                    <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {stat.badge} {language === 'en' ? 'new' : 'baru'}
                    </span>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-stone-900">{stat.value}</p>
                        <p className="text-sm text-stone-500">{stat.title}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Recent Inquiries */}
        <Card data-testid="recent-inquiries-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">
              {language === 'en' ? 'Recent Inquiries' : 'Pertanyaan Terbaru'}
            </CardTitle>
            <Link 
              to="/admin/inquiries" 
              className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              {language === 'en' ? 'View All' : 'Lihat Semua'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {recentInquiries.length > 0 ? (
              <div className="space-y-4">
                {recentInquiries.map((inquiry, index) => (
                  <div 
                    key={inquiry.id}
                    className="flex items-center justify-between p-4 bg-stone-50 rounded-lg"
                    data-testid={`recent-inquiry-${index}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-stone-900 truncate">{inquiry.name}</p>
                      <p className="text-sm text-stone-500 truncate">{inquiry.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        inquiry.status === 'new' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : inquiry.status === 'contacted'
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-stone-100 text-stone-700'
                      }`}>
                        {inquiry.status}
                      </span>
                      <div className="flex items-center gap-1 text-stone-400 text-sm">
                        <Clock className="w-4 h-4" />
                        {formatDate(inquiry.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                {language === 'en' ? 'No inquiries yet' : 'Belum ada pertanyaan'}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link to="/admin/services" data-testid="quick-action-services">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <Settings className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-medium text-stone-900">
                    {language === 'en' ? 'Manage Services' : 'Kelola Layanan'}
                  </h3>
                  <p className="text-sm text-stone-500">
                    {language === 'en' ? 'Add or edit IT & Construction services' : 'Tambah atau edit layanan IT & Konstruksi'}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-stone-400 ml-auto group-hover:text-emerald-600 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/translations" data-testid="quick-action-translations">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <Languages className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-stone-900">
                    {language === 'en' ? 'Update Translations' : 'Perbarui Terjemahan'}
                  </h3>
                  <p className="text-sm text-stone-500">
                    {language === 'en' ? 'Manage EN/ID translations' : 'Kelola terjemahan EN/ID'}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-stone-400 ml-auto group-hover:text-purple-600 transition-colors" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/inquiries" data-testid="quick-action-inquiries">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center group-hover:bg-sky-200 transition-colors">
                  <MessageSquare className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-medium text-stone-900">
                    {language === 'en' ? 'View Inquiries' : 'Lihat Pertanyaan'}
                  </h3>
                  <p className="text-sm text-stone-500">
                    {language === 'en' ? 'Respond to customer inquiries' : 'Tanggapi pertanyaan pelanggan'}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-stone-400 ml-auto group-hover:text-sky-600 transition-colors" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
